import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { TWIN_LAYERS } from "../lib/data";
import {
  TOPOLOGY_DRILLDOWN, RULE_EFFECTIVENESS, RULE_AUDIT_LOG, DATA_STREAMS, LAKEHOUSE_STATS,
  PROCESS_MINING, FEED_SIGNALS, SCENARIO_LIBRARY, SCENARIO_CATEGORIES,
} from "../lib/dataTwin";
import { Tile, Badge, SectionHeader, StatusDot } from "../components/Primitives";
import { formatPeso, formatNumber, pct, cn } from "../lib/utils";
import { useCopilot } from "../lib/copilot.jsx";
import { useRole } from "../lib/role.jsx";
import * as Icons from "lucide-react";

const LAYER_DEFS = [
  { id: "L1", label: "Topology", icon: "Network" },
  { id: "L2", label: "Policy & Rules", icon: "Layers" },
  { id: "L3", label: "Operational State", icon: "Cpu" },
  { id: "L4", label: "Process & Workflow", icon: "Workflow" },
  { id: "L5", label: "External Environment", icon: "Cloud" },
  { id: "SCN", label: "AI Scenario Library", icon: "Sparkles" },
];

export default function DigitalTwin() {
  const [active, setActive] = useState("L1");

  return (
    <div className="space-y-6 animate-fade-in">
      <SectionHeader eyebrow="PART VI · Operational Digital Twin" title="Virtual 1Rotary Retail Network"
        description="A continuously-synced virtual replica across five context layers + an AI Scenario Library. Drill into every node, rule, stream, process, feed and scenario." />

      <div className="grid grid-cols-12 gap-6">
        {/* Layer rail */}
        <div className="col-span-12 lg:col-span-3 space-y-2">
          {LAYER_DEFS.map((l, i) => {
            const Icon = Icons[l.icon];
            const isActive = active === l.id;
            const layerMeta = TWIN_LAYERS.find(x => x.id === l.id);
            return (
              <button key={l.id} data-testid={`twin-layer-${l.id}`}
                onClick={() => setActive(l.id)}
                className={cn("w-full text-left tile p-4 transition-all relative group",
                  isActive ? "border-primary glow-primary" : "hover:border-border-strong")}>
                <div className="flex items-start gap-3">
                  <div className={cn("w-10 h-10 rounded-sm flex items-center justify-center shrink-0",
                    isActive ? "bg-primary text-primary-fg" : "bg-surface-2 text-muted")}>
                    <Icon size={18} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-mono-tabular text-[10px] text-subtle">{l.id === "SCN" ? "AI" : l.id}</span>
                      {isActive && <Badge status="info">Active</Badge>}
                    </div>
                    <div className="font-display font-semibold mt-0.5">{l.label}</div>
                    <p className="text-[11px] text-muted leading-snug mt-1 line-clamp-2">
                      {l.id === "SCN" ? "12 pre-built scenarios — climate, macro, competitive, supply-chain, network" : layerMeta?.description}
                    </p>
                  </div>
                  <Icons.ChevronRight size={14} className={cn("text-muted shrink-0 mt-3 transition-transform", isActive && "rotate-90 text-primary")} />
                </div>
              </button>
            );
          })}
        </div>

        {/* Content panel */}
        <div className="col-span-12 lg:col-span-9 space-y-6">
          {active === "L1" && <L1Topology />}
          {active === "L2" && <L2Policy />}
          {active === "L3" && <L3State />}
          {active === "L4" && <L4Process />}
          {active === "L5" && <L5External />}
          {active === "SCN" && <ScenarioLibrary />}
        </div>
      </div>
    </div>
  );
}

/* ---------------- L1 — Topology with drill-down ---------------- */
function L1Topology() {
  const layer = TWIN_LAYERS.find(l => l.id === "L1");
  const [selected, setSelected] = useState("HQ");
  const detail = TOPOLOGY_DRILLDOWN[selected];

  const hq = layer.nodes.find(n => n.id === "HQ");
  const l1 = layer.nodes.filter(n => n.level === 1);
  const l2 = layer.nodes.filter(n => n.level === 2);

  return (
    <>
      <AiBanner
        title="Knowledge Graph & Alert Routing"
        score={0.91}
        body="The AI routes 4,184 alerts/month through this topology using a learned escalation graph. Click any node to inspect routing confidence, recent decisions, and policy guardrails."
      />
      <Tile title="Organizational Topology" action={<Badge status="info">Click node to drill-down</Badge>}>
        <div className="flex flex-col items-center gap-3">
          <NodeCard node={hq} size="lg" selected={selected === "HQ"} onClick={() => setSelected("HQ")} />
          <div className="w-px h-6 bg-border" />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 w-full">
            {l1.map(n => <NodeCard key={n.id} node={n} selected={selected === n.id} onClick={() => setSelected(n.id)} />)}
          </div>
          <div className="w-px h-6 bg-border" />
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2 w-full">
            {l2.map(n => <NodeCard key={n.id} node={n} size="sm" selected={selected === n.id} onClick={() => setSelected(n.id)} />)}
          </div>
        </div>
      </Tile>

      <Tile testid="topology-drilldown" title={`Drill-down — ${detail.title}`}>
        <div className="grid grid-cols-3 gap-3 mb-4">
          {Object.entries(detail.metrics).map(([k, v]) => (
            <div key={k} className="p-3 border border-border rounded-sm bg-surface-2/40">
              <div className="kpi-label">{labelize(k)}</div>
              <div className="kpi-value text-xl mt-1">{typeof v === "number" && v > 1_000_000 ? formatPeso(v, { compact: true }) : formatNumber(v)}</div>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-2 gap-3 mb-3">
          <Section label="AI Routing Stats">
            <Row k="Confidence" v={`${(detail.aiRouting.confidence * 100).toFixed(0)}%`} good />
            <Row k="Alerts Routed (30d)" v={formatNumber(detail.aiRouting.alertsRouted)} />
            <Row k="Decisions Logged" v={formatNumber(detail.aiRouting.decisionsLogged)} />
          </Section>
          <Section label="Key Policies">
            {detail.keyPolicies.map((p, i) => <div key={i} className="text-[11px] text-muted py-0.5">• {p}</div>)}
          </Section>
        </div>
        <Section label="Recent Decisions (last 30d)">
          {detail.recentDecisions.map((d, i) => <div key={i} className="text-[11px] text-fg/90 py-0.5">→ {d}</div>)}
        </Section>
      </Tile>
    </>
  );
}

function NodeCard({ node, size = "md", selected, onClick }) {
  const cls = { lg: "p-3", md: "p-2.5", sm: "p-2 text-xs" }[size];
  return (
    <button onClick={onClick} data-testid={`node-${node.id}`}
      className={cn("border rounded-sm text-center transition-colors w-full",
        selected ? "border-primary bg-primary/10" : "border-border bg-surface-2 hover:bg-surface", cls)}>
      <div className="font-mono-tabular text-[9px] text-subtle uppercase tracking-widest">{node.id}</div>
      <div className={cn("font-display font-semibold mt-1", size === "lg" ? "text-sm" : size === "sm" ? "text-[11px]" : "text-xs")}>{node.label}</div>
      {node.count > 1 && <div className="text-[10px] text-muted mt-0.5">{node.count} entities</div>}
    </button>
  );
}

/* ---------------- L2 — Policy & Rules ---------------- */
function L2Policy() {
  const [selected, setSelected] = useState("R3");
  const rule = RULE_EFFECTIVENESS.find(r => r.code === selected);
  const totalSavings = RULE_EFFECTIVENESS.reduce((s, r) => s + r.savings, 0);

  return (
    <>
      <AiBanner title="Rule Effectiveness & AI Recommender" score={0.88}
        body={`Policies guard against bad decisions but also miss revenue. AI tracks ${RULE_EFFECTIVENESS.reduce((s,r)=>s+r.triggered,0).toLocaleString()} triggers, ${totalSavings.toLocaleString()} ₱ saved, and proposes ${RULE_EFFECTIVENESS.length} adjustments based on observed patterns.`} />

      <Tile title="Policy & Rules" action={<Badge status="info">{RULE_EFFECTIVENESS.length} active · AI proposes 6 adjustments</Badge>}>
        <div className="space-y-2">
          {RULE_EFFECTIVENESS.map(r => {
            const overrideRate = r.overridden / r.triggered;
            const isSelected = selected === r.code;
            return (
              <button key={r.code} onClick={() => setSelected(r.code)}
                data-testid={`rule-${r.code}`}
                className={cn("w-full text-left p-3 border rounded-sm transition-colors",
                  isSelected ? "border-primary bg-primary/5" : "border-border bg-surface-2/40 hover:bg-surface-2")}>
                <div className="flex items-center gap-3">
                  <div className={cn("w-9 h-9 rounded-sm flex items-center justify-center font-mono-tabular text-xs font-semibold shrink-0",
                    isSelected ? "bg-primary text-primary-fg" : "bg-primary/10 text-primary")}>{r.code}</div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium">{r.name}</span>
                      <Badge status="success">active</Badge>
                      {overrideRate > 0.20 && <Badge status="warning">{(overrideRate * 100).toFixed(0)}% overridden</Badge>}
                    </div>
                    <div className="text-[11px] text-muted mt-0.5">Last changed {r.lastChanged} · {formatNumber(r.triggered)} triggers · {formatPeso(r.savings, { compact: true })} saved</div>
                  </div>
                  <Icons.ChevronRight size={14} className={cn("text-muted shrink-0", isSelected && "rotate-90 text-primary")} />
                </div>
              </button>
            );
          })}
        </div>
      </Tile>

      <Tile testid="rule-drilldown" title={`AI Recommender — ${rule.name}`}>
        <div className="grid grid-cols-4 gap-3 mb-4">
          <Stat label="Triggered (30d)" value={formatNumber(rule.triggered)} />
          <Stat label="Overridden" value={`${formatNumber(rule.overridden)} (${((rule.overridden / rule.triggered) * 100).toFixed(0)}%)`} />
          <Stat label="₱ Saved" value={formatPeso(rule.savings, { compact: true })} status="success" />
          <Stat label="AI Confidence" value={`${(rule.confidence * 100).toFixed(0)}%`} status="info" />
        </div>
        <div className="p-3 bg-primary/5 border border-primary/30 rounded-sm flex items-start gap-2.5">
          <Icons.Sparkles size={14} className="text-primary mt-0.5 shrink-0" />
          <div className="text-[12px] leading-relaxed flex-1">
            <strong className="text-primary">AI Suggested Adjustment:</strong> {rule.aiSuggestion}
          </div>
          <button data-testid={`rule-apply-${rule.code}`} className="px-3 py-1 text-[11px] border border-primary/40 text-primary hover:bg-primary/10 rounded-sm shrink-0">Propose to Governance</button>
        </div>
      </Tile>

      <Tile title="Audit Log — last 5 changes">
        <table className="w-full text-sm">
          <thead><tr className="text-[10px] uppercase tracking-widest text-muted border-b border-border">
            <th className="py-2 px-3 text-left">Timestamp</th><th className="py-2 px-3">Code</th><th className="py-2 px-3 text-left">Action</th><th className="py-2 px-3 text-left">Actor</th><th className="py-2 px-3 text-left">Detail</th>
          </tr></thead>
          <tbody className="font-mono-tabular">
            {RULE_AUDIT_LOG.map((l, i) => (
              <tr key={i} className="border-b border-border" data-testid={`audit-${i}`}>
                <td className="py-2 px-3 text-muted text-[11px]">{l.ts}</td>
                <td className="py-2 px-3 text-center"><Badge status="neutral">{l.code}</Badge></td>
                <td className="py-2 px-3 font-sans">{l.action}</td>
                <td className="py-2 px-3 font-sans text-[11px] text-muted">{l.actor}</td>
                <td className="py-2 px-3 font-sans text-[11px]">{l.detail}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Tile>
    </>
  );
}

/* ---------------- L3 — Operational State with stream drill-down ---------------- */
function L3State() {
  const layer = TWIN_LAYERS.find(l => l.id === "L3");
  const [selected, setSelected] = useState(DATA_STREAMS[0].id);
  const stream = DATA_STREAMS.find(s => s.id === selected);

  return (
    <>
      <AiBanner title="Data Quality & Lakehouse Health" score={LAKEHOUSE_STATS.pipelineHealth}
        body={`8 streams · ${DATA_STREAMS.reduce((s, x) => s + x.rowsPerDay, 0).toLocaleString()} rows/day · Bronze ${LAKEHOUSE_STATS.bronze.volumeGb}GB → Silver ${LAKEHOUSE_STATS.silver.volumeGb}GB → Gold ${LAKEHOUSE_STATS.gold.volumeGb}GB. AI flags ${DATA_STREAMS.filter(s => s.qualityScore < 0.92).length} streams below quality SLO.`} />

      <Tile title="Live Operational State">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          {layer.state.map(s => (
            <div key={s.label} className="border border-border rounded-sm p-3 bg-surface-2/40">
              <div className="kpi-label">{s.label}</div>
              <div className="kpi-value text-xl mt-1.5">{s.value}</div>
              <div className="mt-1.5 flex items-center gap-1.5 text-[10px] text-success"><span className="live-dot" /> Synced</div>
            </div>
          ))}
        </div>
      </Tile>

      <Tile title="Data Stream Catalog">
        <table className="w-full text-sm">
          <thead><tr className="text-[10px] uppercase tracking-widest text-muted border-b border-border">
            <th className="py-2 px-3 text-left">Stream</th><th className="py-2 px-3 text-left">Source</th><th className="py-2 px-3">Layer</th>
            <th className="py-2 px-3 text-right">Rows/Day</th><th className="py-2 px-3 text-right">Latency</th><th className="py-2 px-3 text-right">Quality</th>
          </tr></thead>
          <tbody className="font-mono-tabular">
            {DATA_STREAMS.map(s => (
              <tr key={s.id} onClick={() => setSelected(s.id)}
                  data-testid={`stream-${s.id}`}
                  className={cn("border-b border-border cursor-pointer hover:bg-surface-2",
                    selected === s.id && "bg-primary/5")}>
                <td className="py-2 px-3 font-sans"><div className="font-medium">{s.name}</div><div className="text-[10px] text-muted">{s.freq}</div></td>
                <td className="py-2 px-3 font-sans text-[11px] text-muted">{s.source}</td>
                <td className="py-2 px-3 text-center"><Badge status={s.layer === "Gold" ? "success" : s.layer === "Silver" ? "info" : "neutral"}>{s.layer}</Badge></td>
                <td className="py-2 px-3 text-right">{formatNumber(s.rowsPerDay, { compact: true })}</td>
                <td className="py-2 px-3 text-right">{s.latencySec < 60 ? `${s.latencySec}s` : `${(s.latencySec / 60).toFixed(0)}m`}</td>
                <td className={cn("py-2 px-3 text-right font-semibold", s.qualityScore >= 0.95 ? "text-success" : s.qualityScore >= 0.90 ? "text-info" : "text-warning")}>
                  {(s.qualityScore * 100).toFixed(0)}%
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Tile>

      <Tile testid="stream-drilldown" title={`Drill-down — ${stream.name}`}>
        <div className="grid grid-cols-4 gap-3">
          <Stat label="Completeness" value={`${(stream.completeness * 100).toFixed(1)}%`} status={stream.completeness >= 0.99 ? "success" : "warning"} />
          <Stat label="Freshness" value={`${(stream.freshness * 100).toFixed(0)}%`} status={stream.freshness >= 0.9 ? "success" : "warning"} />
          <Stat label="Anomaly Rate" value={`${(stream.anomalyRate * 100).toFixed(1)}%`} status={stream.anomalyRate < 0.02 ? "success" : "warning"} />
          <Stat label="Composite Quality" value={`${(stream.qualityScore * 100).toFixed(0)}%`} status="info" />
        </div>
        <div className="mt-4 p-3 bg-surface-2 border border-border rounded-sm text-[12px] leading-relaxed">
          <strong className="text-fg">Lakehouse path:</strong> {stream.source} → Bronze raw → Silver curated → Gold dimensional. Schema versioned in Unity Catalog. Quality SLO: ≥95% (composite). Current: <strong className={stream.qualityScore >= 0.95 ? "text-success" : "text-warning"}>{(stream.qualityScore * 100).toFixed(0)}%</strong>.
        </div>
      </Tile>
    </>
  );
}

/* ---------------- L4 — Process & Workflow with mining drill-down ---------------- */
function L4Process() {
  const [selected, setSelected] = useState(PROCESS_MINING[0].name);
  const proc = PROCESS_MINING.find(p => p.name === selected);

  return (
    <>
      <AiBanner title="Process Mining & Cycle-time AI" score={0.84}
        body={`AI compares actual vs designed cycle time across 4 critical processes. Detected ${PROCESS_MINING.filter(p => p.deviation > 0.3).length} processes with >30% deviation. Potential ₱${(PROCESS_MINING.reduce((s, p) => s + p.aiSavingsIfFixed, 0) / 1e6).toFixed(1)}M annual savings if optimized.`} />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {PROCESS_MINING.map(p => (
          <button key={p.name} onClick={() => setSelected(p.name)}
            data-testid={`process-${p.name.replace(/\s/g, '-')}`}
            className={cn("text-left tile p-3 transition-all",
              selected === p.name ? "border-primary glow-primary" : "hover:border-border-strong")}>
            <div className="flex items-center justify-between mb-2">
              <span className="font-display font-semibold text-sm">{p.name}</span>
              <Badge status={p.deviation > 0.4 ? "critical" : p.deviation > 0.2 ? "warning" : "success"}>+{(p.deviation * 100).toFixed(0)}%</Badge>
            </div>
            <div className="flex items-baseline gap-2 text-[11px] font-mono-tabular">
              <span className="text-muted">Actual</span>
              <span className="text-fg font-semibold">{p.cycleTimeHrsActual < 24 ? `${p.cycleTimeHrsActual}h` : `${(p.cycleTimeHrsActual / 24).toFixed(1)}d`}</span>
              <span className="text-muted">vs Designed</span>
              <span className="text-success">{p.cycleTimeHrsDesigned < 24 ? `${p.cycleTimeHrsDesigned}h` : `${(p.cycleTimeHrsDesigned / 24).toFixed(1)}d`}</span>
            </div>
          </button>
        ))}
      </div>

      <Tile testid="process-drilldown" title={`Process Mining — ${proc.name}`}>
        <div className="space-y-1.5">
          {(() => {
            const maxHrs = Math.max(...proc.steps.map(s => s.hrs));
            return proc.steps.map((s, i) => {
              const designedPct = (s.designed / maxHrs) * 100;
              const actualPct = (s.hrs / maxHrs) * 100;
              return (
                <div key={i} className="flex items-center gap-3" data-testid={`process-step-${i}`}>
                  <div className="w-40 shrink-0">
                    <div className="text-sm font-medium">{s.name}</div>
                    <div className="text-[10px] text-muted font-mono-tabular">{s.hrs < 24 ? `${s.hrs}h` : `${(s.hrs / 24).toFixed(1)}d`} / designed {s.designed < 24 ? `${s.designed}h` : `${(s.designed / 24).toFixed(1)}d`}</div>
                  </div>
                  <div className="flex-1 h-6 bg-surface-2 rounded-sm overflow-hidden flex items-center relative">
                    {/* Actual cycle-time bar */}
                    <div className={cn("h-full absolute left-0 top-0",
                         s.status === "bottleneck" ? "bg-critical/55" :
                         s.status === "warning" ? "bg-warning/55" : "bg-success/40")}
                         style={{ width: `${actualPct}%` }} title={`Actual ${s.hrs}h`} />
                    {/* Designed marker line */}
                    <div className="h-full absolute top-0 border-l-2 border-success"
                         style={{ left: `${designedPct}%` }} title={`Designed ${s.designed}h`} />
                  </div>
                  <Badge status={s.status === "bottleneck" ? "critical" : s.status === "warning" ? "warning" : "success"}>{s.status}</Badge>
                </div>
              );
            });
          })()}
        </div>
        <div className="mt-4 p-3 bg-primary/5 border border-primary/30 rounded-sm flex items-start gap-2.5">
          <Icons.Sparkles size={14} className="text-primary mt-0.5 shrink-0" />
          <div className="text-[12px] leading-relaxed flex-1">
            <strong className="text-primary">AI Insight:</strong> {proc.aiInsight}
            <div className="text-success font-semibold mt-1">Potential annual savings if fixed: {formatPeso(proc.aiSavingsIfFixed, { compact: true })}</div>
          </div>
        </div>
      </Tile>
    </>
  );
}

/* ---------------- L5 — External feeds with signal correlation ---------------- */
function L5External() {
  const layer = TWIN_LAYERS.find(l => l.id === "L5");
  const [selected, setSelected] = useState(FEED_SIGNALS[0].name);
  const signal = FEED_SIGNALS.find(f => f.name === selected);

  return (
    <>
      <AiBanner title="Signal Correlation & Demand Prediction" score={0.86}
        body="6 external feeds drive 8 internal demand models. AI continuously re-fits correlation strength and lead-time. Heat index leads HVAC demand by ~14 days (R²=0.81)." />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {layer.feeds.map((f, i) => {
          const sig = FEED_SIGNALS[i];
          const isSel = sig?.name && selected === sig.name;
          return (
            <button key={f.name} onClick={() => sig?.name && setSelected(sig.name)}
              data-testid={`feed-${i}`}
              className={cn("tile p-3 text-left transition-all", isSel && "border-primary glow-primary")}>
              <div className="flex items-start gap-3">
                <StatusDot status={f.status} className="mt-1.5" />
                <div className="flex-1 min-w-0">
                  <div className="kpi-label">{f.name}</div>
                  <div className="kpi-value text-base mt-1">{f.value}</div>
                  <div className={cn("text-[11px] mt-1 font-mono-tabular",
                    f.status === "critical" && "text-critical",
                    f.status === "warning" && "text-warning",
                    f.status === "success" && "text-success",
                    f.status === "info" && "text-info")}>{f.trend}</div>
                  {sig && (
                    <div className="text-[10px] text-primary mt-1">Signal R² {sig.correl.toFixed(2)} · {sig.leadDays}d lead</div>
                  )}
                </div>
                <Badge status={f.status}>{f.status}</Badge>
              </div>
            </button>
          );
        })}
      </div>

      {signal && (
        <Tile testid="feed-drilldown" title={`Signal Analysis — ${signal.name}`}>
          <div className="grid grid-cols-3 gap-3 mb-3">
            <Stat label="Correlation (R²)" value={signal.correl.toFixed(2)} status={signal.correl > 0.6 ? "success" : signal.correl > 0.4 ? "info" : "warning"} />
            <Stat label="Lead Time" value={`${signal.leadDays} days`} />
            <Stat label="Model Confidence" value={`${(signal.confidence * 100).toFixed(0)}%`} status="info" />
          </div>
          <div className="p-3 bg-surface-2 border border-border rounded-sm text-[12px] leading-relaxed">
            <strong className="text-fg">Sensitivity:</strong> {signal.sensitivity}
          </div>
        </Tile>
      )}
    </>
  );
}

/* ---------------- AI Scenario Library ---------------- */
function ScenarioLibrary() {
  const navigate = useNavigate();
  const { askCopilot } = useCopilot();
  const { canAccess } = useRole();
  const [category, setCategory] = useState("all");
  const [selected, setSelected] = useState(SCENARIO_LIBRARY[0].id);

  const filtered = useMemo(() =>
    category === "all" ? SCENARIO_LIBRARY : SCENARIO_LIBRARY.filter(s => s.category === category),
    [category]);

  const scenario = SCENARIO_LIBRARY.find(s => s.id === selected) || filtered[0];

  return (
    <>
      <AiBanner title="AI Scenario Library" score={0.85}
        body={`${SCENARIO_LIBRARY.length} scenarios across ${SCENARIO_CATEGORIES.length - 1} categories. Each scenario is auto-triggered by signal monitors and includes parameters, predicted outcomes, and recommended AI actions.`} />

      {/* Category filter */}
      <div className="tile p-3 flex flex-wrap gap-2" data-testid="scenario-categories">
        {SCENARIO_CATEGORIES.map(c => (
          <button key={c.id} onClick={() => setCategory(c.id)}
            data-testid={`cat-${c.id}`}
            className={cn("text-xs px-3 h-8 border rounded-sm transition-colors",
              category === c.id ? "bg-primary text-primary-fg border-primary" : "bg-surface-2 border-border hover:bg-surface")}>
            {c.name} {c.id !== "all" && <span className="ml-1 text-[10px] opacity-60">({SCENARIO_LIBRARY.filter(s => s.category === c.id).length})</span>}
          </button>
        ))}
      </div>

      <div className="grid grid-cols-12 gap-4">
        {/* List */}
        <div className="col-span-12 md:col-span-5 space-y-2 max-h-[820px] overflow-y-auto pr-1">
          {filtered.map(s => {
            const Icon = Icons[s.icon] || Icons.Sparkles;
            const isSel = s.id === selected;
            return (
              <button key={s.id} onClick={() => setSelected(s.id)}
                data-testid={`scenario-card-${s.id}`}
                className={cn("w-full text-left tile p-3 transition-all",
                  isSel ? "border-primary glow-primary" : "hover:border-border-strong")}>
                <div className="flex items-start gap-3">
                  <div className={cn("w-9 h-9 rounded-sm flex items-center justify-center shrink-0",
                    isSel ? "bg-primary text-primary-fg" :
                    s.urgency === "high" ? "bg-critical/15 text-critical" :
                    s.urgency === "medium" ? "bg-warning/15 text-warning" : "bg-info/15 text-info")}>
                    <Icon size={15} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge status="neutral">{s.category}</Badge>
                      <Badge status={s.urgency === "high" ? "critical" : s.urgency === "medium" ? "warning" : "info"}>{s.urgency}</Badge>
                    </div>
                    <div className="font-display font-semibold text-sm mt-1">{s.name}</div>
                    <div className="text-[10px] text-muted mt-0.5">Trigger: {s.triggerSignal}</div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>

        {/* Detail */}
        <div className="col-span-12 md:col-span-7" data-testid="scenario-detail">
          <Tile title={scenario.name}
                action={<div className="flex items-center gap-2">
                  <Badge status="info">{(scenario.confidence * 100).toFixed(0)}% confidence</Badge>
                  <button data-testid="ask-copilot-scenario"
                    onClick={() => askCopilot(
                      `As CEO, walk me through the AI scenario "${scenario.name}" (${scenario.category}, urgency: ${scenario.urgency}). Trigger signal: ${scenario.triggerSignal}. Parameters: ${scenario.parameters.map(p => `${p.name}=${p.value} (vs baseline ${p.baseline})`).join("; ")}. Predicted outcomes: revenue impact ${formatPeso(scenario.expectedOutcomes.revenue, { compact: true })}, stockout-risk ${scenario.expectedOutcomes.stockoutRisk > 0 ? "+" : ""}${(scenario.expectedOutcomes.stockoutRisk * 100).toFixed(0)}%, margin impact ${(scenario.expectedOutcomes.marginImpact * 100).toFixed(1)}pp, affected branches ${scenario.expectedOutcomes.branches}. AI recommends: ${scenario.aiActions.join("; ")}. (1) How realistic is this scenario for 1Rotary in 2026? (2) Which AI actions should I sequence first and which executive owns each? (3) What second-order effects am I missing? (4) Walk me through the cash-flow and supplier-relationship implications.`
                    )}
                    className="inline-flex items-center gap-1 text-[11px] text-primary hover:underline font-medium"><Icons.MessageSquare size={11} /> Brief me →</button>
                </div>}>
            <div className="p-3 bg-surface-2 border border-border rounded-sm mb-3 flex items-start gap-2.5">
              <Icons.Bell size={14} className="text-warning mt-0.5 shrink-0" />
              <div className="text-[12px] leading-relaxed"><strong className="text-fg">Trigger Signal:</strong> {scenario.triggerSignal}</div>
            </div>

            <div className="text-[10px] uppercase tracking-widest text-muted font-semibold mb-2">Parameters</div>
            <table className="w-full text-sm mb-4">
              <thead><tr className="text-[10px] uppercase tracking-widest text-muted border-b border-border">
                <th className="py-1.5 px-2 text-left">Parameter</th><th className="py-1.5 px-2 text-right">Value</th><th className="py-1.5 px-2 text-right">Baseline</th><th className="py-1.5 px-2 text-left">Impact</th>
              </tr></thead>
              <tbody className="font-mono-tabular">
                {scenario.parameters.map((p, i) => (
                  <tr key={i} className="border-b border-border" data-testid={`param-${i}`}>
                    <td className="py-2 px-2 font-sans font-medium text-[12px]">{p.name}</td>
                    <td className="py-2 px-2 text-right text-primary font-semibold">{p.value}</td>
                    <td className="py-2 px-2 text-right text-muted">{p.baseline}</td>
                    <td className="py-2 px-2 font-sans text-[11px] text-muted">{p.impact}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="text-[10px] uppercase tracking-widest text-muted font-semibold mb-2">Expected Outcomes</div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
              <Stat label="Revenue Impact" value={formatPeso(scenario.expectedOutcomes.revenue, { compact: true })} status={scenario.expectedOutcomes.revenue >= 0 ? "success" : "critical"} />
              <Stat label="Stockout Risk Δ" value={`${scenario.expectedOutcomes.stockoutRisk >= 0 ? "+" : ""}${(scenario.expectedOutcomes.stockoutRisk * 100).toFixed(0)}%`} status={scenario.expectedOutcomes.stockoutRisk > 0.1 ? "critical" : "info"} />
              <Stat label="Margin Δ" value={`${scenario.expectedOutcomes.marginImpact >= 0 ? "+" : ""}${(scenario.expectedOutcomes.marginImpact * 100).toFixed(1)}pp`} status={scenario.expectedOutcomes.marginImpact >= 0 ? "success" : "warning"} />
              <Stat label="Branches Affected" value={scenario.expectedOutcomes.branches} />
            </div>

            <div className="text-[10px] uppercase tracking-widest text-muted font-semibold mb-2">AI-Recommended Actions</div>
            <div className="space-y-1.5">
              {scenario.aiActions.map((a, i) => (
                <div key={i} className="flex items-start gap-2 text-[12px] p-2 bg-surface-2/40 border border-border rounded-sm" data-testid={`action-${i}`}>
                  <span className={cn("w-5 h-5 rounded-sm flex items-center justify-center font-mono-tabular text-[10px] font-semibold shrink-0",
                    "bg-primary/15 text-primary")}>{i + 1}</span>
                  <span className="text-fg/90">{a}</span>
                </div>
              ))}
            </div>

            <div className="mt-4 flex gap-2">
              {canAccess("/workbench") && (
                <button data-testid="run-in-workbench"
                  onClick={() => navigate("/workbench")}
                  className="px-3 py-2 text-xs font-semibold bg-primary text-primary-fg rounded-sm hover:opacity-90">Open in Simulation Workbench →</button>
              )}
              <button className="px-3 py-2 text-xs font-semibold border border-border rounded-sm hover:bg-surface-2" data-testid="save-watchlist">Save to Watchlist</button>
            </div>
          </Tile>
        </div>
      </div>
    </>
  );
}

/* ---------------- Helper components ---------------- */
function AiBanner({ title, score, body }) {
  return (
    <div className="tile p-4 border-primary/30 bg-primary/5" data-testid="ai-banner">
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-sm bg-primary/15 text-primary flex items-center justify-center shrink-0">
          <Icons.Sparkles size={16} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-display font-semibold text-sm text-primary">{title}</span>
            <Badge status="info">AI Layer Score {(score * 100).toFixed(0)}%</Badge>
          </div>
          <p className="text-[12px] text-fg/90 leading-relaxed mt-1">{body}</p>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, status }) {
  return (
    <div className="p-2.5 border border-border rounded-sm bg-surface-2/40">
      <div className="kpi-label flex items-center gap-1.5">{status && <StatusDot status={status} />}{label}</div>
      <div className="kpi-value text-base mt-1">{value}</div>
    </div>
  );
}

function Section({ label, children }) {
  return (
    <div className="border border-border rounded-sm p-3 bg-surface-2/40">
      <div className="text-[10px] uppercase tracking-widest text-muted font-semibold mb-2">{label}</div>
      {children}
    </div>
  );
}

function Row({ k, v, good }) {
  return (
    <div className="flex items-center justify-between text-[11px] py-0.5">
      <span className="text-muted">{k}</span>
      <span className={cn("font-mono-tabular font-semibold", good && "text-success")}>{v}</span>
    </div>
  );
}

function labelize(k) {
  return k.replace(/([A-Z])/g, " $1").replace(/^./, c => c.toUpperCase()).trim();
}
